import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar, User, Clock, Users } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import ClientDayView from './ClientDayView';
import ClientDayViewMobile from './ClientDayViewMobile';
import ClientWeekView from './ClientWeekView';
import ClientWeekViewMobile from './ClientWeekViewMobile';
import ClientMonthView from './ClientMonthView';
import { useTheme } from '../contexts/ThemeContext';
import '../Planning.css';

const ClientPlanning = ({ db, client, hasPermission }) => {
  // Vérification de sécurité pour client null
  if (!client) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6">Planning client</h1>
        <div className="text-center p-8">
          <User size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">Client non sélectionné</h2>
          <p className="text-gray-600">
            Veuillez sélectionner un client depuis la liste des bénéficiaires pour accéder au planning.
          </p>
        </div>
      </div>
    );
  }

  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [clientStats, setClientStats] = useState({
    regularEmployees: [],
    monthlyHoursAvg: 0,
    startDate: null,
    totalAppointments: 0
  });

  // Calculer les statistiques du client
  useEffect(() => {
    const fetchClientStats = async () => {
      if (!db || !client?.id) return;

      try {
        // Récupérer tous les rendez-vous du client
        const appointmentsRef = collection(db, 'appointments');
        const q = query(appointmentsRef, where('clientId', '==', client.id));
        const querySnapshot = await getDocs(q);

        const appointments = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date()
        }));

        // Calculer les intervenants habituels (ceux qui ont le plus de rendez-vous)
        // Regrouper par NOM d'employé pour éviter les doublons d'IDs
        const employeeCountsByName = {};
        let totalHours = 0;
        let firstAppointmentDate = null;

        appointments.forEach(apt => {
          // Compter les rendez-vous par nom d'employé (au lieu de l'ID)
          if (apt.employeeName) {
            employeeCountsByName[apt.employeeName] = (employeeCountsByName[apt.employeeName] || 0) + 1;
          }

          // Calculer les heures totales
          if (apt.startTime && apt.endTime) {
            const start = apt.startTime.split(':');
            const end = apt.endTime.split(':');
            const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
            const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
            totalHours += (endMinutes - startMinutes) / 60;
          }

          // Trouver le premier rendez-vous
          if (!firstAppointmentDate || apt.date < firstAppointmentDate) {
            firstAppointmentDate = apt.date;
          }
        });

        // Trier les employés par nombre de rendez-vous et prendre les 3 premiers
        const regularEmployees = Object.entries(employeeCountsByName)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count], index) => ({
            id: `employee-${index}`,
            name: name,
            count: count
          }));

        // Calculer la moyenne d'heures mensuelles
        let monthlyHoursAvg = 0;
        if (firstAppointmentDate) {
          const monthsDiff = Math.max(1, Math.floor((new Date() - firstAppointmentDate) / (1000 * 60 * 60 * 24 * 30)));
          monthlyHoursAvg = Math.round((totalHours / monthsDiff) * 10) / 10;
        }

        setClientStats({
          regularEmployees,
          monthlyHoursAvg,
          startDate: firstAppointmentDate,
          totalAppointments: appointments.length
        });
      } catch (error) {
        // Erreur silencieuse lors du calcul des statistiques
      }
    };

    fetchClientStats();
  }, [db, client?.id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      switch (currentView) {
        case 'day':
          newDate.setDate(newDate.getDate() - 1);
          break;
        case 'week':
          newDate.setDate(newDate.getDate() - 7);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() - 1);
          break;
        default:
          break;
      }
      return newDate;
    });
  }, [currentView]);

  const goToNext = useCallback(() => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      switch (currentView) {
        case 'day':
          newDate.setDate(newDate.getDate() + 1);
          break;
        case 'week':
          newDate.setDate(newDate.getDate() + 7);
          break;
        case 'month':
          newDate.setMonth(newDate.getMonth() + 1);
          break;
        default:
          break;
      }
      return newDate;
    });
  }, [currentView]);

  const formatDateRange = useCallback(() => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    switch (currentView) {
      case 'day':
        return currentDate.toLocaleDateString('fr-FR', options);
      case 'week':
        const weekStart = new Date(currentDate);
        const dayOfWeek = weekStart.getDay();
        const startOfWeek = new Date(weekStart);
        // Correction du calcul du début de semaine
        startOfWeek.setDate(weekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const weekEnd = new Date(startOfWeek);
        weekEnd.setDate(startOfWeek.getDate() + 6);
        return `${startOfWeek.toLocaleDateString('fr-FR', options)} - ${weekEnd.toLocaleDateString('fr-FR', options)}`;
      case 'month':
        return currentDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
      default:
        return '';
    }
  }, [currentView, currentDate]);

  const formatDate = useCallback((date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
  }, []);

  const formatTime = useCallback((time) => {
    return time;
  }, []);

  const renderMonthView = useCallback(() => {
    return (
      <ClientMonthView
        key={currentDate.toISOString()} // Ajout d'une clé unique
        db={db}
        client={client}
        currentDate={currentDate}
        formatDate={formatDate}
        formatTime={formatTime}
        onClose={() => { }}
        onAppointmentClick={() => { }}
        onAddAppointment={() => { }}
        onUpdateAppointment={() => { }}
        onDeleteAppointment={() => { }}
        hasPermission={hasPermission}
      />
    );
  }, [currentDate, db, client, formatDate, formatTime]);

  return (
    <div className="pv-container">
      <div className="flex items-center justify-between mb-4">
        <h2 className="pv-title mb-0">{client.lastName} {client.firstName}</h2>
      </div>

      {/* Section des statistiques */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Intervenants habituels */}
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Users size={20} className="text-blue-600 dark:text-blue-300" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Intervenants habituels</p>
              {clientStats.regularEmployees.length > 0 ? (
                <div className="space-y-1">
                  {clientStats.regularEmployees.map((emp, index) => (
                    <div key={emp.id} className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {index + 1}. {emp.name} <span className="text-xs text-gray-500 dark:text-gray-400">({emp.count} rdv)</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Aucun intervenant assigné</p>
              )}
            </div>
          </div>

          {/* Heures mensuelles moyennes */}
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Clock size={20} className="text-green-600 dark:text-green-300" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Heures mensuelles moyennes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {clientStats.monthlyHoursAvg}h
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Basé sur {clientStats.totalAppointments} rendez-vous
              </p>
            </div>
          </div>

          {/* Date de début du suivi */}
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Calendar size={20} className="text-purple-600 dark:text-purple-300" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bénéficiaire depuis</p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {clientStats.startDate
                  ? clientStats.startDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                  : 'Non défini'}
              </p>
              {clientStats.startDate && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Il y a {Math.floor((new Date() - clientStats.startDate) / (1000 * 60 * 60 * 24 * 30))} mois
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pv-controls">
        <div className="pv-view-buttons">
          <button onClick={() => setCurrentView('day')} className={`pv-button ${currentView === 'day' ? 'pv-button-primary' : ''}`}>Jour</button>
          <button onClick={() => setCurrentView('week')} className={`pv-button ${currentView === 'week' ? 'pv-button-primary' : ''}`}>Semaine</button>
          <button onClick={() => setCurrentView('month')} className={`pv-button ${currentView === 'month' ? 'pv-button-primary' : ''}`}>Mois</button>
        </div>
        <div className="pv-date-navigation">
          <button onClick={goToPrevious} className="pv-button pv-button-icon"><ChevronLeft size={18} /></button>
          <span className="pv-date-range">{formatDateRange()}</span>
          <button onClick={goToNext} className="pv-button pv-button-icon"><ChevronRight size={18} /></button>
        </div>
        <button onClick={goToToday} className="pv-button pv-today-button">
          <Calendar size={16} />
          <span>Aujourd'hui</span>
        </button>
      </div>
      <div className="pv-view-container">
        {currentView === 'day' && (
          isMobile ? (
            <ClientDayViewMobile
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={formatDate}
              formatTime={formatTime}
              hasPermission={hasPermission}
            />
          ) : (
            <ClientDayView
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={formatDate}
              formatTime={formatTime}
              hasPermission={hasPermission}
            />
          )
        )}
        {currentView === 'week' && (
          isMobile ? (
            <ClientWeekViewMobile
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={formatDate}
              formatTime={formatTime}
              hasPermission={hasPermission}
            />
          ) : (
            <ClientWeekView
              db={db}
              client={client}
              currentDate={currentDate}
              formatDate={formatDate}
              formatTime={formatTime}
              hasPermission={hasPermission}
            />
          )
        )}
        {currentView === 'month' && renderMonthView()}
      </div>
    </div>
  );
};

export default ClientPlanning;
